import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Cloud, Loader2, Lock, Mail, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/staffApi";
import { screenMessage } from "../lib/screenMessage";
import { inAppPath } from "../lib/inAppPath";
import useNoIndex from "../lib/useNoIndex";

/* Staff sign-in, separate from the customer login. Email and password first; an authenticator
   (or backup) code only after the password is accepted and the account has 2FA. */
const AdminLogin = () => {
  const navigate = useNavigate();
  useNoIndex();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [needsCode, setNeedsCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const finish = (data) => navigate(inAppPath(data?.next, "/admin"));

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/login", { email, password });
      if (data?.requires_2fa) {
        setNeedsCode(true);
        return;
      }
      finish(data);
    } catch {
      setError(screenMessage("login"));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/login", { email, password, code: code.trim() });
      finish(data);
    } catch {
      setError(screenMessage("twofa"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background px-5" data-testid="admin-login-page">
      <div className="pointer-events-none absolute inset-0 bg-dots opacity-50" />
      <div className="relative w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-xl">
        <div className="flex flex-col items-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Cloud className="h-6 w-6" strokeWidth={2.2} />
          </span>
          <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Restricted access</p>
          <h1 className="mt-2 font-display text-2xl font-bold text-foreground">
            {needsCode ? "Enter your authenticator code" : "Staff & Admin sign-in"}
          </h1>
          <p className="mt-1 text-center text-sm text-muted-foreground">
            {needsCode
              ? "Type the 6-digit code from your authenticator app, or a backup code."
              : "Customer accounts should use the regular sign-in."}
          </p>
        </div>

        {needsCode ? (
          <form onSubmit={submitCode} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="admin-code" className="text-sm">Authenticator code</Label>
              <div className="relative mt-1.5">
                <ShieldCheck className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="admin-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  className="pl-9"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  data-testid="admin-login-code"
                />
              </div>
            </div>
            {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
            <Button type="submit" disabled={busy || code.trim().length < 6} className="w-full rounded-full bg-primary font-semibold">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Verify & sign in
            </Button>
            <button
              type="button"
              className="w-full text-center text-xs text-muted-foreground hover:text-foreground"
              onClick={() => { setNeedsCode(false); setCode(""); setError(""); }}
            >
              Use a different account
            </button>
          </form>
        ) : (
          <form onSubmit={submitPassword} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="admin-email" className="text-sm">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="admin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="pl-9"
                  autoComplete="username"
                  required
                  data-testid="admin-login-email"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="admin-password" className="text-sm">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="admin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  className="pl-9"
                  autoComplete="current-password"
                  required
                  data-testid="admin-login-password"
                />
              </div>
            </div>
            {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-primary font-semibold">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Continue
            </Button>
          </form>
        )}

        <p className="mt-5 text-center text-sm text-muted-foreground">
          <Link to="/login" className="hover:underline">Regular sign-in</Link>
          {" · "}
          <Link to="/admin/setup" className="hover:underline" data-testid="admin-login-setup-link">First owner setup</Link>
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
